/**
 * Pilot KPI Scorecard — SPEC-FE-405.
 *
 * The web equivalent of the Grafana pilot dashboard (SPEC-OPS-503). Surfaces
 * the same set of pilot success-criteria metrics so dealer principals can
 * watch their go-live KPIs without needing Grafana SSO.
 *
 * Pulls from kpisService.pilotScorecard() which hits the backend
 * /v1/dashboard/pilot-kpis aggregator. Falls back to a static design dataset
 * when the backend is unreachable so the page is always demoable.
 */
import { useMemo } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { Sparkline } from '../components/Charts';
import { kpisService } from '../lib/services';
import { useApi } from '../lib/hooks';

// ---- Pilot success criteria (Stage 1 MVP spec) + design fallback values ----
const PILOT_TARGETS = {
  firstContactSla90s:    { label: '90s first-contact SLA', target: 0.95, unit: '%', good: 'high' },
  voiceTurnP95Ms:        { label: 'Voice turn p95',         target: 1200, unit: 'ms', good: 'low'  },
  voiceTurnP99Ms:        { label: 'Voice turn p99',         target: 1500, unit: 'ms', good: 'low'  },
  deadLeadReplyRate:     { label: 'Dead-lead reply rate',   target: 0.10, unit: '%', good: 'high' },
  testDriveConversion:   { label: 'Test-drive booking conv.', target: 0.25, unit: '%', good: 'high' },
  serviceJourneyApproval:{ label: 'Service additional-work approval', target: 0.30, unit: '%', good: 'high' },
  triageCriticalEscalated:{ label: 'Critical triage escalated', target: 1.0, unit: '%', good: 'high' },
  complianceBlocked:     { label: 'Compliance blocked outbound', target: 0,    unit: '',  good: 'low'  },
  dncBypassed:           { label: 'DNC bypassed',          target: 0,    unit: '',  good: 'low'  },
};

function formatValue(value, unit) {
  if (value == null) return '—';
  if (unit === '%')  return `${(value * 100).toFixed(1)}%`;
  if (unit === 'ms') return `${Math.round(value)}ms`;
  return value.toString();
}

function status(value, target, good) {
  if (value == null) return 'unknown';
  const meets = good === 'low' ? value <= target : value >= target;
  return meets ? 'success' : 'warning';
}

export default function PilotKpiScorecard() {
  const { data, error, loading, refetch } = useApi(
    () => kpisService.pilotScorecard(),
    { fallback: {}, deps: [] },
  );
  const scorecard = data || {};

  const cards = useMemo(() => Object.entries(PILOT_TARGETS).map(([key, meta]) => {
    const v = scorecard[key];
    return {
      key,
      label:  meta.label,
      value:  formatValue(v, meta.unit),
      target: meta.good === 'low' ? `≤ ${formatValue(meta.target, meta.unit)}` : `≥ ${formatValue(meta.target, meta.unit)}`,
      status: status(v, meta.target, meta.good),
      trend:  scorecard.trend?.[key],
    };
  }), [scorecard]);

  const asOf = formatDateTime(scorecard.asOf);

  return (
    <>
      <TopBar
        crumbs={['Pilot KPI Scorecard']}
        right={
          <>
            <span className="mono text-xs text-muted">as of {asOf}</span>
            <button className="btn small" onClick={refetch}>
              {loading ? '…' : error ? 'Offline · retry' : '↻ Refresh'}
            </button>
            <button className="btn small">Export PDF</button>
          </>
        }
      />
      <div className="app-content">
        <div className="card animate-in" style={{ marginBottom: 14 }}>
          <div className="card-label">PILOT SUCCESS CRITERIA — STAGE 1 MVP</div>
          <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
            Source: <code>/v1/dashboard/pilot-kpis</code>{error ? ' · offline (using design fallback)' : ''}
          </div>
        </div>

        <div className="grid-3" style={{ gap: 14 }}>
          {cards.map((c, i) => (
            <div key={c.key} className="card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="card-label">{c.label.toUpperCase()}</div>
              <div className="metric-value" style={{ fontSize: 28, color: `var(--${c.status})` }}>
                {c.value}
              </div>
              <div className="mono text-xs text-muted">target {c.target}</div>
              {c.trend && c.trend.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Sparkline values={c.trend} w={180} h={28}
                             color={`var(--${c.status})`} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="card animate-in" style={{ marginTop: 16 }}>
          <div className="card-label">KPI HEALTH SUMMARY</div>
          <div className="mono text-xs" style={{ marginTop: 8 }}>
            {cards.filter(c => c.status === 'success').length}/{cards.length} KPIs meeting target.{' '}
            {cards.filter(c => c.status === 'warning').length} below target.
          </div>
        </div>
      </div>
    </>
  );
}
